import Cookies from "js-cookie";
import { debounce } from "lodash";
import { message } from "antd";
import querystring from "querystring";
import { history } from "store";
import { cache } from "swr";
import { appConfigService } from "./appConfigService";
import { extractJwtPayload } from "utils/auth";
import { getAccessTokenByCustomerId } from "pages/Auth/utils";
import { isMicroApp } from "utils/microApp";

const API_URL = (function () {
  if (process.env.NODE_ENV === "development") {
    return process.env.REACT_APP_API_URL;
  }
  return !isMicroApp
    ? window.location.origin + process.env.REACT_APP_API_URL
    : process.env.REACT_APP_ENV === "production"
    ? "https://affiliate.tiki.vn/api/v1"
    : "https://affiliate.tala.xyz/api/v1";
})();

const publicPath = ["/login", "/register"];

export interface RestAPIClientConfig {
  path?: string;
  endpoint?: string;
  auth?: boolean;
}

class RestAPIClient {
  public static getToken = async () => {
    try {
      const payload = extractJwtPayload(RestAPIClient.token);
      const now = Date.now().valueOf();
      const isExpired = now >= payload.exp * 1000;
      if (!isExpired) {
        return !RestAPIClient.token ? "" : RestAPIClient.token;
      } else {
        // refresh token
        const newToken = await getAccessTokenByCustomerId(payload.customer_id);
        if (newToken) {
          console.log("Token refreshed");
          RestAPIClient.token = newToken;
          return newToken;
        }
        return "";
      }
    } catch (e) {
      return "";
    }
  };

  static token?: string;

  static seller_id?: string;

  protected api_url = API_URL;

  protected path: string;

  protected auth: boolean;

  private static process401 = debounce(
    () => {
      if (isMicroApp) return;
      RestAPIClient.token = undefined;
      Cookies.remove("customer_id");
      if (!publicPath.includes(window.location.pathname)) {
        cache.clear();
        history.replace(
          `/login?redirect=${encodeURIComponent(window.location.pathname)}`
        );
      }
    },
    1000,
    { leading: true, trailing: false }
  );

  private static process30x = debounce(() => {
    message.error("Session Expired. Please login again!");
    // setTimeout(() => {
    //   oauthService.invalidOAuth();
    //   window.location.reload();
    // }, 3000);
  }, 1000);

  constructor(config: string | RestAPIClientConfig, auth: boolean = true) {
    this.path = (typeof config === "string" ? config : config?.path) || "";
    if (typeof config !== "string") {
      if (config?.endpoint) this.api_url = config.endpoint;
    }
    this.querystring = querystring;
    this.auth = auth;
  }

  protected querystring: typeof querystring;

  private async request(
    contextPath: string,
    method: string,
    payload?: any,
    opts?: {
      token?: string;
      type?: "json" | "blob";
    }
  ) {
    const token = (await RestAPIClient.getToken()) || opts?.token;
    if (this.auth && (!token || token === "undefined")) {
      RestAPIClient.process401();
      throw new Error("UNAUTHORIZED");
    }

    let url = `${this.api_url}/${this.path}${contextPath}`;

    if (method === "GET" && payload) {
      url = `${url}?${querystring.stringify(payload || {})}`;
    }

    const lang = appConfigService.getItem("lang");

    const options: RequestInit = {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": lang === "vi" ? "vi-VN" : "en-US",
        // "Content-Type": "application/json",
      },
      credentials: "include",
      redirect: "follow",
    };

    if (
      method === "POST" ||
      method === "PUT" ||
      method === "PATCH" ||
      method === "DELETE"
    ) {
      options["body"] =
        payload instanceof FormData ? payload : JSON.stringify(payload);
    }

    const urlInstance = new URL(url);

    if (RestAPIClient.seller_id && !urlInstance.searchParams.has("seller_id")) {
      urlInstance.searchParams.append("seller_id", RestAPIClient.seller_id);
    }

    const res = await fetch(urlInstance.toString(), options);
    try {
      switch (true) {
        case res.status === 401: {
          RestAPIClient.process401();
          const json = await res?.json();
          if (!!json) {
            const error = {
              ...json,
              status: res.status,
            };
            throw error;
          } else {
            const error = {
              message: res.statusText,
              status: res.status,
            };
            throw error;
          }
        }
        case res.status === 400: {
          const json = await res?.json();
          throw json;
        }
        case res.status === 301 ||
          res.status === 302 ||
          res.type === "opaqueredirect": {
          // RestAPIClient.process30x();
          break;
        }
        case res.status > 201:
          const json = await res?.json();
          if (!!json) {
            const error = {
              ...json,
              status: res.status,
            };
            throw error;
          } else {
            const error = {
              message: res.statusText,
              status: res.status,
            };
            throw error;
          }

        case res.status === 200 || res.status === 201: {
          if (opts?.type === "blob") {
            const result = await res.blob();
            return result;
          }
          const json = await res.json();
          if (json.success !== true && this.api_url === API_URL) {
            throw json;
          }
          return json;
        }
      }
    } catch (e) {
      throw e;
    }
  }

  protected get = (
    contextPath = "",
    params?: object,
    opts?: {
      token?: string;
    }
  ) => this.request(contextPath, "GET", params, opts);

  protected post = (
    contextPath = "",
    body: object | FormData = {},
    opts: {
      token?: string;
      type?: "blob" | "json";
    } = {}
  ) => this.request(contextPath, "POST", body, opts);

  protected put = (contextPath = "", body: object = {}) =>
    this.request(contextPath, "PUT", body);

  protected patch = (contextPath = "", body: object = {}) =>
    this.request(contextPath, "PATCH", body);

  protected delete = (contextPath = "", body: object = {}) =>
    this.request(contextPath, "DELETE", body);

  protected download = async (contextPath = "", params?: any) => {
    const token = await RestAPIClient.getToken();
    if (!token) {
      RestAPIClient.process401();
      throw new Error("UNAUTHORIZED");
    }

    let url = `${API_URL}/${this.path}${contextPath}`;

    if (params) {
      url = `${url}?${querystring.stringify(params || {})}`;
    }

    const lang = appConfigService.getItem("lang");

    const options: RequestInit = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": lang === "vi" ? "vi-VN" : "en-US",
        // "Content-Type": "application/json",
      },
      credentials: "include",
      redirect: "follow",
    };

    const res = await fetch(url, options);

    try {
      switch (true) {
        case res.status === 401: {
          RestAPIClient.process401();
          const json = await res?.json();
          if (!!json) {
            const error = {
              ...json,
              status: res.status,
            };
            throw error;
          } else {
            const error = {
              message: res.statusText,
              status: res.status,
            };
            throw error;
          }
        }
        case res.status === 301 ||
          res.status === 302 ||
          res.type === "opaqueredirect": {
          // RestAPIClient.process30x();
          break;
        }
        case res.status > 201:
          const json = await res?.json();
          if (!!json) {
            const error = {
              ...json,
              status: res.status,
            };
            throw error;
          } else {
            const error = {
              message: res.statusText,
              status: res.status,
            };
            throw error;
          }

        case res.status === 200 || res.status === 201: {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          return url;
        }
      }
    } catch (e) {
      throw e;
    }
  };

  protected upload = async (
    contextPath: string,
    payload: any = {},
    onProgress?: any
  ) => {
    const token = await RestAPIClient.getToken();
    return new Promise((res, rej) => {
      try {
        let url = `${API_URL}/${this.path}${contextPath}`;
        let xhr = new XMLHttpRequest();

        const lang = appConfigService.getItem("lang");

        const headers: any = {
          Authorization: `Bearer ${token}`,
          "Accept-Language": lang === "vi" ? "vi-VN" : "en-US",
        };
        xhr.open("post", url);
        for (let k in headers || {}) xhr.setRequestHeader(k, headers[k]);
        xhr.onload = (e: any) => {
          try {
            res(JSON.parse(e.target.responseText));
          } catch (e) {
            rej(e);
          }
        };
        xhr.onerror = rej;
        if (xhr.upload && onProgress) xhr.upload.onprogress = onProgress; // event.loaded / event.total * 100 ; //event.lengthComputable
        xhr.send(payload);
      } catch (e) {
        console.log(e);
        rej(e);
      }
    });
  };
}

export default RestAPIClient;
